import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Inbox, Loader2, Download, Search, RefreshCw, Phone, Mail, AlertTriangle,
  CheckCircle2, Copy, Calendar,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Lead Kutusu — müşteri sitelerindeki form doldurumları.
// Veri yolu: müşteri sitesi (GTM etiketi) → POST /api/leads/v1/collect → lead_inbox_leads.
// Site kendi formunu kendi sunucusuna göndermeye devam eder; biz KOPYA alırız.
// Bu yüzden "son lead" nabzı önemli: site kodu değişirse kopya sessizce susar,
// panelde tablo boş görünür ama hata çıkmaz.

const API = 'https://api.glowytics.com';
const BASE = `${API}/api/leads/v1`;

const DURUMLAR = [
  { key: 'yeni', label: 'Yeni', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'arandi', label: 'Arandı', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'ulasilamadi', label: 'Ulaşılamadı', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  { key: 'uye_oldu', label: 'Üye oldu', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'ilgilenmiyor', label: 'İlgilenmiyor', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
];
const durumMeta = (k) => DURUMLAR.find((d) => d.key === k) || DURUMLAR[0];

const KAYNAK_ADI = { contact: 'İletişim formu', uyeol: 'Üye Ol formu', bmi: 'BMI formu', meta_form: 'Meta lead formu', bilinmiyor: '—' };

function gunBasi(gunOnce) {
  const d = new Date();
  d.setDate(d.getDate() - gunOnce);
  return d.toISOString().slice(0, 10);
}

function tarihYaz(v) {
  if (!v) return '—';
  const d = new Date(v);
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// 🔴 new URL() bozuk/eksik referrer'da FIRLATIR ve tüm tabloyu düşürürdü.
// Referrer kullanıcı tarayıcısından geliyor, biçimi garanti değil.
function alanAdi(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch (e) { return null; }
}

function gecenSure(v) {
  if (!v) return null;
  const dk = Math.round((Date.now() - new Date(v).getTime()) / 60000);
  if (dk < 60) return `${dk} dk önce`;
  if (dk < 1440) return `${Math.round(dk / 60)} saat önce`;
  return `${Math.round(dk / 1440)} gün önce`;
}

export default function LeadInbox() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;

  const [sites, setSites] = useState([]);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(null);
  const [indiriliyor, setIndiriliyor] = useState(false);

  const [from, setFrom] = useState(gunBasi(30));
  const [to, setTo] = useState(gunBasi(0));
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');

  const filtreQS = useMemo(() => {
    const p = new URLSearchParams({ brandId: brandId || '', from, to });
    if (q.trim()) p.set('q', q.trim());
    if (status) p.set('status', status);
    if (source) p.set('source', source);
    return p.toString();
  }, [brandId, from, to, q, status, source]);

  const yukle = useCallback(async () => {
    if (!brandId) return;
    setLoading(true); setHata(null);
    try {
      const [sr, lr] = await Promise.all([
        fetch(`${BASE}/sites?brandId=${brandId}`, { credentials: 'include' }),
        fetch(`${BASE}/leads?${filtreQS}`, { credentials: 'include' }),
      ]);
      if (!sr.ok || !lr.ok) throw new Error((await lr.json().catch(() => ({}))).error || 'Liste alınamadı');
      const sj = await sr.json(); const lj = await lr.json();
      setSites(sj.sites || []);
      setLeads(lj.leads || []);
      setTotal(lj.total || 0);
    } catch (e) {
      setHata(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, filtreQS]);

  useEffect(() => { yukle(); }, [yukle]);

  const durumDegistir = async (lead, yeni) => {
    const eski = lead.status;
    setLeads((xs) => xs.map((x) => (x.id === lead.id ? { ...x, status: yeni } : x)));
    try {
      const r = await fetch(`${BASE}/leads/${lead.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: yeni }),
      });
      if (!r.ok) throw new Error('kaydedilemedi');
    } catch (e) {
      setLeads((xs) => xs.map((x) => (x.id === lead.id ? { ...x, status: eski } : x)));
      toast.error('Durum kaydedilemedi');
    }
  };

  const notKaydet = async (lead, not) => {
    if ((lead.note || '') === not) return;
    setLeads((xs) => xs.map((x) => (x.id === lead.id ? { ...x, note: not } : x)));
    try {
      const r = await fetch(`${BASE}/leads/${lead.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: not }),
      });
      if (!r.ok) throw new Error('kaydedilemedi');
    } catch (e) { toast.error('Not kaydedilemedi'); }
  };

  // CSV'yi <a href> ile açamayız: uç çerezli kimlik istiyor ve dosya adını
  // Content-Disposition veriyor. Blob'a indirip tarayıcıya kendimiz veriyoruz.
  const csvIndir = async () => {
    setIndiriliyor(true);
    try {
      const r = await fetch(`${BASE}/leads.csv?${filtreQS}`, { credentials: 'include' });
      if (!r.ok) throw new Error('İndirilemedi');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `leadler-${to}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); } finally { setIndiriliyor(false); }
  };

  const kaynaklar = useMemo(() => Array.from(new Set(leads.map((l) => l.source).filter(Boolean))), [leads]);
  const sonLead = sites.reduce((acc, s) => (s.last_lead_at && (!acc || s.last_lead_at > acc) ? s.last_lead_at : acc), null);

  if (!brandId) {
    return <div className="p-8 text-gray-600">Önce bir marka seçin.</div>;
  }

  if (!loading && !sites.length) {
    return (
      <div className="p-8">
        <div className="max-w-xl rounded-xl border border-gray-200 bg-white p-6">
          <Inbox className="w-6 h-6 text-gray-400 mb-3" />
          <h1 className="text-[15px] font-semibold text-gray-900">Lead Kutusu</h1>
          <p className="mt-2 text-sm text-gray-600">
            {selectedBrand?.name} için tanımlı bir site yok. Form doldurumlarını buraya taşımak için
            markaya bir site anahtarı açılması ve müşteri sitesindeki GTM'e toplama etiketinin
            konması gerekiyor.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Inbox className="w-5 h-5 text-indigo-600" />
          <div>
            <h1 className="text-[15px] font-semibold text-gray-900">Lead Kutusu</h1>
            <p className="text-xs text-gray-500">
              {selectedBrand?.name} · müşteri sitesindeki formlardan gelen kayıtlar
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={yukle} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Yenile
          </button>
          <button onClick={csvIndir} disabled={indiriliyor || !leads.length}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white hover:bg-gray-800 disabled:opacity-40">
            {indiriliyor ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}Excel'e aktar
          </button>
        </div>
      </div>

      {/* Nabız: site anahtarı ve en son ne zaman lead düştüğü. Etiket sitede
          silinirse burası sessizce eskir — tablo boş kalır, hata çıkmaz. */}
      <div className="grid gap-3 sm:grid-cols-3">
        {sites.map((s) => (
          <div key={s.key} className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="text-[13px] font-medium text-gray-900">{s.label || s.key}</div>
              <span className={`text-[11px] px-1.5 py-0.5 rounded border ${s.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                {s.active ? 'açık' : 'kapalı'}
              </span>
            </div>
            <div className="mt-2 text-2xl font-semibold text-gray-900">{s.lead_count}</div>
            <div className="text-xs text-gray-500">toplam lead</div>
            <div className="mt-2 flex items-center gap-1.5 text-xs">
              {s.last_lead_at
                ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /><span className="text-gray-600">son lead {gecenSure(s.last_lead_at)}</span></>
                : <><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /><span className="text-gray-600">henüz lead gelmedi</span></>}
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(s.key); toast.success('Site anahtarı kopyalandı'); }}
              className="mt-3 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800">
              <Copy className="w-3 h-3" />site anahtarı
            </button>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ad, telefon, e-posta"
              className="w-56 rounded-lg border border-gray-200 py-1.5 pl-8 pr-2 text-xs" />
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Calendar className="w-3.5 h-3.5" />
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs" />
            <span>–</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
            <option value="">tüm durumlar</option>
            {DURUMLAR.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <select value={source} onChange={(e) => setSource(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
            <option value="">tüm formlar</option>
            {kaynaklar.map((k) => <option key={k} value={k}>{KAYNAK_ADI[k] || k}</option>)}
          </select>
          <span className="ml-auto text-xs text-gray-500">{total} kayıt{sonLead ? ` · son ${gecenSure(sonLead)}` : ''}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />Yükleniyor…
          </div>
        ) : hata ? (
          <div className="p-6 text-sm text-rose-600">{hata}</div>
        ) : !leads.length ? (
          <div className="p-10 text-center text-sm text-gray-500">Bu aralıkta kayıt yok.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-medium">Tarih</th>
                  <th className="px-3 py-2 font-medium">Kişi</th>
                  <th className="px-3 py-2 font-medium">Form</th>
                  <th className="px-3 py-2 font-medium">Kaynak</th>
                  <th className="px-3 py-2 font-medium">Durum</th>
                  <th className="px-3 py-2 font-medium">Not</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const meta = durumMeta(l.status);
                  const kaynak = [l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(' / ');
                  return (
                    <tr key={l.id} className="border-t border-gray-100 align-top hover:bg-gray-50/60">
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">{tarihYaz(l.created_at)}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{l.name || '—'}</div>
                        <div className="mt-0.5 flex flex-col gap-0.5 text-xs text-gray-600">
                          {l.phone && <a href={`tel:${l.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1 hover:text-indigo-600"><Phone className="w-3 h-3" />{l.phone}</a>}
                          {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-indigo-600"><Mail className="w-3 h-3" />{l.email}</a>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{KAYNAK_ADI[l.source] || l.source}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {kaynak || (l.gclid ? 'Google Ads (gclid)' : l.fbclid ? 'Meta (fbclid)' : alanAdi(l.referrer) || 'doğrudan')}
                        {l.gclid && kaynak ? <div className="text-[11px] text-gray-400">gclid var</div> : null}
                      </td>
                      <td className="px-3 py-2">
                        <select value={l.status} onChange={(e) => durumDegistir(l, e.target.value)}
                          className={`rounded-md border px-1.5 py-1 text-[11px] ${meta.cls}`}>
                          {DURUMLAR.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input defaultValue={l.note || ''} placeholder="not…"
                          onBlur={(e) => notKaydet(l, e.target.value)}
                          className="w-44 rounded-md border border-transparent px-1.5 py-1 text-xs hover:border-gray-200 focus:border-gray-300 focus:outline-none" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
